import {ChangeEvent, FormEvent, useEffect, useState} from "react"
import axios from "axios"
import {Alert, Button, Form, Modal, Spinner} from "react-bootstrap"

import {useAuth} from "../../../context/AuthProvider"

type UploadResponse = {
    duplicate: boolean
    document: {
        _id: string
        filename: string
        bytes: number
        interpretationStatus: string
    }
    prompt: string
}

export default function RoomPlanUploadModal({
    show,
    room,
    onClose,
    onUploaded,
}: {
    show: boolean
    room: {
        _id: string
        identifier: string
        name: string
        building?: {identifier: string}
    } | null
    onClose: () => void
    onUploaded: () => void
}) {
    const auth = useAuth()
    const [file, setFile] = useState<File | null>(null)
    const [sourceUrl, setSourceUrl] = useState("")
    const [sourceLabel, setSourceLabel] = useState("")
    const [uploading, setUploading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [result, setResult] = useState<UploadResponse | null>(null)
    const [copied, setCopied] = useState(false)

    useEffect(() => {
        if (!show) return
        setFile(null)
        setSourceUrl("")
        setSourceLabel("")
        setUploading(false)
        setError(null)
        setResult(null)
        setCopied(false)
    }, [room?._id, show])

    const chooseFile = (event: ChangeEvent<HTMLInputElement>) => {
        setFile(event.target.files?.[0] ?? null)
        setResult(null)
    }

    const submit = async (event: FormEvent) => {
        event.preventDefault()
        if (!room || !file) return
        try {
            setUploading(true)
            setError(null)
            const form = new FormData()
            form.append("pdf", file)
            form.append("sourceUrl", sourceUrl)
            form.append("sourceLabel", sourceLabel)
            const response = await axios.post<UploadResponse>(
                `/api/room-power-planning/rooms/${room._id}/documents`,
                form,
                auth.axiosAuthHeader()
            )
            setResult(response.data)
            onUploaded()
        } catch (uploadError: any) {
            setError(uploadError?.response?.data?.message ??
                "Das PDF konnte nicht hochgeladen werden.")
        } finally {
            setUploading(false)
        }
    }

    const copyPrompt = async () => {
        if (!result) return
        try {
            await navigator.clipboard.writeText(result.prompt)
            setCopied(true)
        } catch {
            setError("Der Prompt konnte nicht in die Zwischenablage kopiert werden.")
        }
    }

    return <Modal show={show} onHide={onClose} size="lg">
        <Modal.Header closeButton>
            <Modal.Title>Sitzplan-PDF hinzufügen</Modal.Title>
        </Modal.Header>
        <Form onSubmit={event => void submit(event)}>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                {!result ? <>
                    <Alert variant="info">
                        <strong>{room?.building?.identifier}{" "}
                            {room?.identifier}</strong>
                        {" – "}{room?.name}
                    </Alert>
                    <Form.Group className="mb-3">
                        <Form.Label>PDF-Datei</Form.Label>
                        <Form.Control required type="file"
                            accept="application/pdf,.pdf"
                            onChange={chooseFile}/>
                        <Form.Text>
                            Maximal 25 MB. Die Datei wird unverändert und mit
                            SHA-256-Prüfsumme gespeichert.
                        </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Quell-URL</Form.Label>
                        <Form.Control type="url" value={sourceUrl}
                            placeholder="https://…"
                            onChange={event => setSourceUrl(event.target.value)}/>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Bezeichnung der Quelle</Form.Label>
                        <Form.Control value={sourceLabel}
                            placeholder="Zum Beispiel Fachbereich oder Webseite"
                            onChange={event => setSourceLabel(event.target.value)}/>
                    </Form.Group>
                </> : <>
                    <Alert variant={result.duplicate ? "info" : "success"}>
                        {result.duplicate
                            ? "Dieses PDF war für den Hörsaal bereits hinterlegt."
                            : `„${result.document.filename}“ wurde gespeichert.`}
                    </Alert>
                    <Form.Group>
                        <Form.Label>
                            Prompt für Codex
                        </Form.Label>
                        <Form.Control as="textarea" rows={14} readOnly
                            value={result.prompt}/>
                    </Form.Group>
                </>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose}>
                    Schließen
                </Button>
                {!result
                    ? <Button type="submit"
                        disabled={uploading || !room || !file}>
                        {uploading
                            ? <><Spinner size="sm" className="me-2"/>
                                Hochladen</>
                            : <><i className="bi bi-upload me-2"/>
                                Hochladen</>}
                    </Button>
                    : <Button onClick={() => void copyPrompt()}>
                        <i className={`bi ${copied
                            ? "bi-check-lg" : "bi-clipboard"} me-2`}/>
                        {copied ? "Kopiert" : "Prompt kopieren"}
                    </Button>}
            </Modal.Footer>
        </Form>
    </Modal>
}
